// Cloudflare R2'ye görsel yükleme servisi.
//
// Akış: tarayıcı /api/upload/presign endpoint'inden imzalı bir PUT adresi ister,
// dosyayı doğrudan R2'ye yükler. R2 anahtarları hiçbir zaman tarayıcıya inmez.

import { auth } from '../firebase/config';

const PRESIGN_ENDPOINT = '/api/upload/presign';

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

interface PresignResponse {
  success: boolean;
  uploadUrl: string;
  publicUrl: string;
  key: string;
  error?: string;
}

async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}

export class ImageUploadService {
  // Tek dosya yükle. Dosyanın çağrı öncesinde sıkıştırılmış olması beklenir.
  static async uploadFile(file: File, folder: string = 'products'): Promise<UploadResult> {
    try {
      const token = await getIdToken();
      if (!token) {
        return {
          success: false,
          error: 'Oturumunuz bulunamadı. Görsel yüklemek için Firebase hesabınızla giriş yapın.',
        };
      }

      // 1) İmzalı yükleme adresi al
      const presignRes = await fetch(PRESIGN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          contentType: file.type,
          size: file.size,
          folder,
        }),
      });

      const presignData: PresignResponse = await presignRes.json();

      if (!presignRes.ok || !presignData.success) {
        return { success: false, error: presignData.error || 'Yükleme adresi alınamadı' };
      }

      // 2) Dosyayı doğrudan R2'ye yükle.
      // Content-Type, imzalanan değerle birebir aynı olmak zorunda.
      const putRes = await fetch(presignData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!putRes.ok) {
        return {
          success: false,
          error: `R2 yüklemesi başarısız (HTTP ${putRes.status}). Bucket CORS ayarlarını kontrol edin.`,
        };
      }

      return { success: true, url: presignData.publicUrl };
    } catch (error: any) {
      return { success: false, error: error?.message || 'Görsel yüklenemedi' };
    }
  }

  // Birden fazla dosyayı sırayla yükler; kısmi başarıyı da raporlar.
  static async uploadMultipleFiles(
    files: File[],
    folder: string = 'products'
  ): Promise<{ success: boolean; urls: string[]; errors: string[] }> {
    const urls: string[] = [];
    const errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const result = await this.uploadFile(files[i], folder);
      if (result.success && result.url) {
        urls.push(result.url);
      } else {
        errors.push(`${files[i].name}: ${result.error}`);
      }
    }

    return { success: errors.length === 0, urls, errors };
  }
}

export default ImageUploadService;
